import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APIs } from '../../utilities/APIs';
import { useAuth } from '../../users/AuthContext';
import ApplicantNotices from '../../components/ApplicantNotices';
import { UnikLog } from '../../assets';
import './ApplicantAuth.css';

const ApplicantRegister = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        nationality: '',
        dateOfBirth: ''
    });
    const [otp, setOtp] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [otpSent, setOtpSent] = useState(false);

    const steps = ['Registration Details', 'Verify Email'];    const handleInputChange = (field) => (e) => {
        setFormData(prev => ({
            ...prev,
            [field]: e.target.value
        }));
    };

    const handleOtpChange = (index, value) => {
        if (value.length <= 1) {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);

            // Auto-focus next input
            if (value && index < 5) {
                const nextInput = document.getElementById(`otp-${index + 1}`);
                if (nextInput) nextInput.focus();
            }
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otp[index] && index > 0) {
            const prevInput = document.getElementById(`otp-${index - 1}`);
            if (prevInput) prevInput.focus();
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text');
        const otpArray = pastedData.split('').slice(0, 6);
        
        const newOtp = [...otp];
        otpArray.forEach((digit, index) => {
            if (index < 6 && /^\d$/.test(digit)) {
                newOtp[index] = digit;
            }
        });
        setOtp(newOtp);

        // Focus the last filled input or the next empty one
        const lastFilledIndex = Math.min(otpArray.length - 1, 5);
        const nextInput = document.getElementById(`otp-${lastFilledIndex}`);
        if (nextInput) nextInput.focus();
    };const handleRegister = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validate required fields
        if (!formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber) {
            setError('Please fill in all required fields (First Name, Last Name, Email, and Phone Number)');
            setLoading(false);
            return;
        }
        setSuccess('');

        try {
            // Debug: Log the data being sent
            console.log('Sending registration data:', formData);
            console.log('API endpoint:', APIs.applicant.register);

            const response = await fetch(APIs.applicant.register, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();
            console.log('Registration response:', data);

            if (response.ok && data.status === 'success') {
                setSuccess('Registration successful! Please check your email for OTP verification.');
                setOtpSent(true);
                setActiveStep(1);
            } else {
                setError(data.message || 'Registration failed');
            }
        } catch (error) {
            setError('Network error. Please try again.');
            console.error('Registration error:', error);
        } finally {
            setLoading(false);
        }
    };    const handleVerifyRegistration = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const otpString = otp.join('');
        if (otpString.length < 6) {
            setError('Please enter the complete 6-digit OTP');
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(APIs.applicant.verifyRegistration, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ 
                    email: formData.email, 
                    otp: otpString 
                })
            });

            const data = await response.json();

            if (response.ok && data.status === 'success') {
                setSuccess('Email verified successfully! Logging you in...');
                
                // Auto-login the user after successful verification
                if (data.data && data.data.token) {
                    login(data.data.token, data.data.refreshToken, data.data.user);
                    setTimeout(() => {
                        navigate('/applicant/dashboard');
                    }, 1500);
                } else {
                    // Fallback if no token is provided - redirect to login
                    setTimeout(() => {
                        navigate('/applicant/login');
                    }, 2000);
                }
            } else {
                setError(data.message || 'Invalid OTP. Please try again.');
            }
        } catch (error) {
            setError('Network error. Please try again.');
            console.error('Verify registration error:', error);
        } finally {
            setLoading(false);
        }
    };    const handleBackToRegistration = () => {
        setActiveStep(0);
        setOtp(['', '', '', '', '', '']);
        setOtpSent(false);
        setError('');
        setSuccess('');
    };

    const handleResendOtp = async () => {
        setLoading(true);
        setError('');
        setSuccess('');

        try {
            const response = await fetch(APIs.applicant.register, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok && data.status === 'success') {
                setSuccess('OTP resent successfully to your email.');
            } else {
                setError(data.message || 'Failed to resend OTP');
            }
        } catch (error) {
            setError('Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };    return (
        <div className="auth-container">
            <div className="auth-form-wrapper">
                <div className="auth-notices-section">
                    <ApplicantNotices page="REGISTER" maxNotices={3} />
                </div>
                
                <div className="auth-form-section">
                    <div className="auth-card">
                        <div className="auth-header">
                            <img src={UnikLog} alt="University Logo" className="auth-logo" />
                            <h1 className="auth-title">Student Registration</h1>
                            <p className="auth-subtitle">Create your applicant account</p>
                        </div>

                        <div className="auth-stepper">
                            {steps.map((label, index) => (
                                <div key={label} className={`step ${index <= activeStep ? 'active' : ''}`}>
                                    <div className="step-number">{index + 1}</div>
                                    <span className="step-label">{label}</span>
                                </div>
                            ))}
                        </div>

                        {error && (
                            <div className="auth-alert error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="auth-alert success">
                                {success}
                            </div>
                        )}

                        {activeStep === 0 && (
                            <form onSubmit={handleRegister} className="auth-form">
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">First Name *</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.firstName}
                                            onChange={handleInputChange('firstName')}
                                            required
                                            disabled={loading}
                                            placeholder="Enter your first name"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Last Name *</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.lastName}
                                            onChange={handleInputChange('lastName')}
                                            required
                                            disabled={loading}
                                            placeholder="Enter your last name"
                                        />
                                    </div>
                                </div>
                                
                                <div className="form-group">
                                    <label className="form-label">Email Address *</label>
                                    <input
                                        type="email"
                                        className="form-input"
                                        value={formData.email}
                                        onChange={handleInputChange('email')}
                                        required
                                        disabled={loading}
                                        placeholder="Enter your email address"
                                    />
                                    <span className="form-help">We'll send you an OTP to verify this email address</span>
                                </div>
                                
                                <div className="form-group">
                                    <label className="form-label">Phone Number *</label>
                                    <input
                                        type="tel"
                                        className="form-input"
                                        value={formData.phoneNumber}
                                        onChange={handleInputChange('phoneNumber')}
                                        required
                                        disabled={loading}
                                        placeholder="Enter your phone number"
                                    />
                                </div>
                                
                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">Nationality</label>
                                        <input
                                            type="text"
                                            className="form-input"
                                            value={formData.nationality}
                                            onChange={handleInputChange('nationality')}
                                            disabled={loading}
                                            placeholder="Enter your nationality (optional)"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Date of Birth</label>
                                        <input
                                            type="date"
                                            className="form-input"
                                            value={formData.dateOfBirth}
                                            onChange={handleInputChange('dateOfBirth')}
                                            disabled={loading}
                                        />
                                    </div>
                                </div>
                                
                                <button
                                    type="submit"
                                    className={`auth-button primary ${loading ? 'loading' : ''}`}
                                    disabled={loading || !formData.firstName || !formData.lastName || !formData.email || !formData.phoneNumber}
                                >
                                    {loading ? (
                                        <>
                                            <div className="loading-spinner"></div>
                                            Registering...
                                        </>
                                    ) : (
                                        'Register & Send OTP'
                                    )}
                                </button>
                            </form>
                        )}

                        {activeStep === 1 && (
                            <form onSubmit={handleVerifyRegistration} className="auth-form">
                                <div className="otp-info">
                                    <p>We've sent a 6-digit OTP to <strong>{formData.email}</strong></p>
                                    <p>Please enter it below to verify your email address.</p>
                                </div>

                                <div className="otp-container">
                                    {otp.map((digit, index) => (
                                        <input
                                            key={index}
                                            id={`otp-${index}`}
                                            type="text"
                                            className="otp-input"
                                            value={digit}
                                            onChange={(e) => handleOtpChange(index, e.target.value)}
                                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                            onPaste={handleOtpPaste}
                                            maxLength="1"
                                            pattern="[0-9]"
                                            disabled={loading}
                                        />
                                    ))}
                                </div>

                                <button
                                    type="submit"
                                    className={`auth-button primary ${loading ? 'loading' : ''}`}
                                    disabled={loading || otp.some(digit => !digit)}
                                >
                                    {loading ? (
                                        <>
                                            <div className="loading-spinner"></div>
                                            Verifying...
                                        </>
                                    ) : (
                                        'Verify Email'
                                    )}
                                </button>

                                <div className="form-actions">
                                    <button
                                        type="button"
                                        className="auth-button secondary"
                                        onClick={handleBackToRegistration}
                                        disabled={loading}
                                    >
                                        Back to Registration
                                    </button>
                                    <button
                                        type="button"
                                        className="auth-button text"
                                        onClick={handleResendOtp}
                                        disabled={loading}
                                    >
                                        Resend OTP
                                    </button>
                                </div>
                            </form>
                        )}

                        <div className="auth-footer">
                            <p>
                                Already have an account?{' '}
                                <a href="/applicant/login" className="auth-link">
                                    Login here
                                </a>
                            </p>
                            <p>
                                Are you a staff member?{' '}
                                <a href="/login" className="auth-link">
                                    Staff Login
                                </a>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ApplicantRegister;
